import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import type { DashboardWidgetConfig } from 'src/types/dashboard';
import GroupedBarChart from 'src/components/dashboard/charts/GroupedBarChart';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import { kpiReducer } from 'src/components/dashboard/DashboardPage/widgets/GroupedBarWidget/helper';

import { getColorMap, getGroupData } from './helpers';

import { styles } from './index.styles';

export interface TrendAnalysisBenchmarkComparisonProps {
  config: DashboardWidgetConfig;
}

const TrendAnalysisBenchmarkComparisonWidget: FC<
  TrendAnalysisBenchmarkComparisonProps
> = ({ config }) => {
  const { data } = useStatistics();
  const theme = useTheme();
  const { initialFocalData, kpiLabels } = useMemo(() => {
    const historicalData = data?.focal_result.historical_data;
    if (!historicalData) return { initialFocalData: {}, kpiLabels: {} };

    return {
      initialFocalData: historicalData.data || {},
      kpiLabels: historicalData.kpi_labels || {},
    };
  }, [data]);
  const kpiList = useMemo(
    () => kpiReducer(kpiLabels, config.questions),
    [kpiLabels, config.questions],
  );
  const [kpiSelection, setKpiSelection] = useState<string[]>(
    Object.keys(kpiList) || [],
  );
  const { historicalData, dataName } = useMemo(() => {
    const historicalData = data?.ancestor_result?.historical_data?.data || {};
    const dataName = data?.ancestor_result?.org_units?.[0]?.name;
    return { historicalData, dataName };
  }, [data]);

  const groupData = useMemo(
    () => getGroupData(initialFocalData, historicalData, kpiList, kpiSelection),
    [initialFocalData, historicalData, kpiSelection, kpiList],
  );

  return (
    <Box sx={{ py: 3, px: 2.75 }}>
      <GroupedBarChart
        config={config}
        colorMap={getColorMap(theme)}
        groupSize={1}
        groupData={groupData}
        kpiList={kpiList}
        groupSelection={kpiSelection}
        setGroupSelection={setKpiSelection}
        labelTextAlign="center"
      />
      <Box sx={(theme) => styles.legendWrapper(theme)}>
        <Stack direction="row">
          <CircleIcon sx={(theme) => styles.legendIcon(theme)} />
          <Typography sx={styles.legendLabel}>{dataName}</Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default TrendAnalysisBenchmarkComparisonWidget;
